/**
 * Search index — fetches /api/sitemap-index.json once, caches in a
 * module-level promise so multiple components mounting in parallel
 * (Sitemap.jsx + Landing.jsx navbar modal) share the same request.
 *
 * v60.99.47 — powers both:
 *   • Inline search on /sitemap (filters the visible groups)
 *   • Modal Cmd-K-style search opened from the homepage navbar
 *
 * The /api/sitemap-index.json endpoint already groups every indexable
 * URL (core, widget_landings, seo_tools, discovery, blog, pseo-when-
 * present) so the same fetch powers both surfaces with zero duplicate
 * crawl logic.
 */
import api from "@/lib/api_client";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache. The Promise is stored (not the resolved value)
// so concurrent first-callers share the same in-flight request rather
// than each issuing their own GET.
let cachedPromise = null;

export function fetchSearchIndex() {
  if (cachedPromise) return cachedPromise;
  cachedPromise = api
    .get(`${API}/sitemap-index.json`)
    .then((res) => res.data)
    .catch((err) => {
      // Reset so a future call retries instead of holding a broken
      // rejected promise forever.
      cachedPromise = null;
      throw err;
    });
  return cachedPromise;
}

/**
 * Flatten the grouped index into a single searchable row list.
 * Each row carries its group id + group label so the UI can show
 * categorisation in the result list.
 */
export function flattenIndex(index) {
  if (!index?.groups) return [];
  const out = [];
  for (const g of index.groups) {
    for (const u of g.urls || []) {
      out.push({
        path: u.path,
        label: u.label,
        group: g.id,
        groupLabel: g.title,
      });
    }
  }
  return out;
}

/**
 * Score and rank rows against a free-text query.
 *
 * Algorithm:
 *   • Lowercase + split query into tokens on whitespace.
 *   • For every token, the row must contain it as a substring of
 *     either `label` or `path`. Missing tokens → row filtered out.
 *   • Per-token score:
 *       label startsWith token           → +10
 *       label contains " token" / "-token" (word boundary) → +6
 *       label contains token (substring) → +4
 *       path contains token              → +2
 *   • Final row score is sum of per-token scores. Ties broken
 *     alphabetically by label.
 *   • Caller can truncate the returned list (e.g. to top 30 for the
 *     modal); we don't slice here so the sitemap page can show all
 *     matches when the query is narrow.
 */
export function scoreRows(rows, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return rows;
  const tokens = q.split(/\s+/).filter(Boolean);
  if (!tokens.length) return rows;
  const out = [];
  for (const r of rows) {
    const label = (r.label || "").toLowerCase();
    const path = (r.path || "").toLowerCase();
    let score = 0;
    let missed = false;
    for (const t of tokens) {
      if (label.startsWith(t)) {
        score += 10;
      } else if (label.includes(" " + t) || label.includes("-" + t)) {
        score += 6;
      } else if (label.includes(t)) {
        score += 4;
      } else if (path.includes(t)) {
        score += 2;
      } else {
        missed = true;
        break;
      }
    }
    if (!missed) out.push({ ...r, _score: score });
  }
  out.sort((a, b) => {
    if (b._score !== a._score) return b._score - a._score;
    return (a.label || "").localeCompare(b.label || "");
  });
  return out;
}
